import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    group: '',
    abo: '',
    flics: [],
    expires: ''
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [statusAction, setStatusAction] = useState(null); // 'block' or 'unblock'
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    clinic: '',
    group: '',
    abo: '',
    flics: [],
    expires: ''
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiClient.get('/api/admin/okta-users');
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchSubscriptions();
  }, []);

  const fetchRoles = async () => {
    try {
      const data = await apiClient.get('/api/admin/roles');
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const data = await apiClient.get('/api/admin/subscriptions');
      setSubscriptions(data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.group?.toLowerCase().includes(searchLower) ||
      user.clinic?.toLowerCase().includes(searchLower) ||
      user.abo?.toLowerCase().includes(searchLower)
    );
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      group: user.group || '',
      abo: user.abo || '',
      flics: user.flics ? user.flics.split(';').filter(f => f.trim()) : [],
      expires: user.expires || ''
    });
    setOpenEditDialog(true);
    setFormError(null);
    setFormSuccess(false);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
    setEditForm({
      group: '',
      abo: '',
      flics: [],
      expires: ''
    });
    setFormError(null);
    setFormSuccess(false);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFlic = () => {
    setEditForm(prev => ({
      ...prev,
      flics: [...prev.flics, '']
    }));
  };

  const handleRemoveFlic = (index) => {
    setEditForm(prev => ({
      ...prev,
      flics: prev.flics.filter((_, i) => i !== index)
    }));
  };

  const handleFlicChange = (index, value) => {
    setEditForm(prev => ({
      ...prev,
      flics: prev.flics.map((flic, i) => i === index ? value : flic)
    }));
  };

  const validateExpirationDate = (dateString) => {
    if (!dateString || dateString.trim() === '') {
      return { isValid: true, error: null }; // Empty is allowed
    }

    // Check format dd.MM.yyyy
    const dateRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const match = dateString.match(dateRegex);
    
    if (!match) {
      return { 
        isValid: false, 
        error: 'Le format doit être dd.MM.yyyy (ex: 31.12.2024)' 
      };
    }

    const [, day, month, year] = match;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    // Check if it's a valid date
    const date = new Date(yearNum, monthNum - 1, dayNum);
    
    if (date.getDate() !== dayNum || 
        date.getMonth() !== monthNum - 1 || 
        date.getFullYear() !== yearNum) {
      return { 
        isValid: false, 
        error: 'Cette date n\'existe pas' 
      };
    }

    // Note: We allow past dates for expiration - they will be highlighted in the table

    return { isValid: true, error: null };
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      // Validate expiration date
      const dateValidation = validateExpirationDate(editForm.expires);
      if (!dateValidation.isValid) {
        setFormError(dateValidation.error);
        setSaving(false);
        return;
      }

      const updateData = {
        app_metadata: {
          group: editForm.group || null,
          abo: editForm.abo || null,
          flics: editForm.flics.filter(f => f.trim()).join(';') || null,
          expires: editForm.expires || null
        }
      };

      await apiClient.put(`/api/admin/okta-users/${selectedUser.id}`, updateData);
      
      setFormSuccess(true);
      handleCloseEditDialog();
      
      // Debounce refresh to ensure Auth0 has processed the changes
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error) {
      console.error('Error updating user:', error);
      setFormError('Impossible de mettre à jour l\'utilisateur');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenStatusDialog = (user, action) => {
    setSelectedUser(user);
    setStatusAction(action);
    setOpenStatusDialog(true);
    setFormError(null);
    setFormSuccess(false);
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setSelectedUser(null);
    setStatusAction(null);
    setFormError(null);
    setFormSuccess(false);
  };

  const handleStatusChange = async () => {
    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      const updateData = {
        blocked: statusAction === 'block'
      };

      await apiClient.patch(`/api/admin/okta-users/${selectedUser.id}/status`, updateData);
      
      setFormSuccess(true);
      handleCloseStatusDialog();
      
      // Debounce refresh to ensure Auth0 has processed the changes
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error) {
      console.error('Error changing user status:', error);
      setFormError(`Impossible de ${statusAction === 'block' ? 'désactiver' : 'réactiver'} l'utilisateur`);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateForm({
      email: '',
      password: '',
      clinic: '',
      group: '',
      abo: '',
      flics: [],
      expires: ''
    });
    setOpenCreateDialog(true);
    setFormError(null);
    setFormSuccess(false);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setCreateForm({
      email: '',
      password: '',
      clinic: '',
      group: '',
      abo: '',
      flics: [],
      expires: ''
    });
    setFormError(null);
    setFormSuccess(false);
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateAddFlic = () => {
    setCreateForm(prev => ({
      ...prev,
      flics: [...prev.flics, '']
    }));
  };

  const handleCreateRemoveFlic = (index) => {
    setCreateForm(prev => ({
      ...prev,
      flics: prev.flics.filter((_, i) => i !== index)
    }));
  };

  const handleCreateFlicChange = (index, value) => {
    setCreateForm(prev => ({
      ...prev,
      flics: prev.flics.map((flic, i) => i === index ? value : flic)
    }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      // Validate required fields
      if (!createForm.email || !createForm.password || !createForm.clinic || !createForm.group || !createForm.abo) {
        setFormError('L\'email, le mot de passe, la clinique, le groupe et l\'abonnement sont obligatoires');
        setSaving(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(createForm.email)) {
        setFormError('Format d\'email invalide');
        setSaving(false);
        return;
      }

      // Validate expiration date
      const dateValidation = validateExpirationDate(createForm.expires);
      if (!dateValidation.isValid) {
        setFormError(dateValidation.error);
        setSaving(false);
        return;
      }

      const createData = {
        email: createForm.email,
        password: createForm.password,
        app_metadata: {
          clinic: createForm.clinic,
          group: createForm.group || null,
          abo: createForm.abo || null,
          flics: createForm.flics.filter(f => f.trim()).join(';') || null,
          expires: createForm.expires || null
        }
      };

      await apiClient.post('/api/admin/okta-users', createData);
      
      setFormSuccess(true);
      handleCloseCreateDialog();
      
      // Debounce refresh to ensure Auth0 has processed the changes
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error) {
      // Extract error message from response
      let errorMessage = 'Impossible de créer l\'utilisateur';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData && typeof errorData === 'object') {
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          }
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
    setFormError(null);
    setFormSuccess(false);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
    setFormError(null);
    setFormSuccess(false);
  };

  const handleDeleteUser = async () => {
    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      await apiClient.delete(`/api/admin/okta-users/${selectedUser.id}`);
      
      setFormSuccess(true);
      handleCloseDeleteDialog();
      
      // Debounce refresh to ensure Auth0 has processed the changes
      setTimeout(() => {
        fetchUsers();
      }, 1000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setFormError('Impossible de supprimer l\'utilisateur');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'suspended':
        return 'warning';
      case 'staged':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const formatCreationDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatFlics = (flics) => {
    if (!flics) return 'N/A';
    return flics.split(';').map(flic => flic.trim()).join('\n');
  };

  const isExpired = (expiresString) => {
    if (!expiresString || expiresString.trim() === '') {
      return false;
    }

    // Parse dd.MM.yyyy format
    const dateRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const match = expiresString.match(dateRegex);
    
    if (!match) {
      return false; // Invalid format, don't highlight
    }

    const [, day, month, year] = match;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    const expirationDate = new Date(yearNum, monthNum - 1, dayNum);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return expirationDate < today;
  };

  if (loading) {
    return (
      <div className="bg-gray-100 rounded-lg">
        <div className="px-6 py-4">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-100 rounded-lg">
        <div className="px-6 py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
          <div className="flex justify-center">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Actualiser
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">
            Utilisateurs Auth0
          </h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleOpenCreateDialog}
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PersonAddIcon className="w-4 h-4 mr-2" />
              Ajouter un utilisateur
            </button>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Actualiser
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Rechercher par email, nom, groupe, clinique..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clinique
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Groupe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Abonnement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  FLICs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière connexion
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => {
                  const createdDate = formatDate(user.created);
                  const lastLoginDate = formatDate(user.lastLogin);
                  const creationDate = formatCreationDate(user.created);
                  const userExpired = isExpired(user.expires);
                  const userBlocked = user.status === 'blocked';
                  // Only highlight in red if expired AND not blocked (since blocked users can't connect anyway)
                  const shouldHighlightExpired = userExpired && !userBlocked;
                  return (
                    <tr key={user.id} className={`hover:bg-gray-50 ${shouldHighlightExpired ? 'bg-red-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.id}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.clinic || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.group || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.abo || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="whitespace-pre-line">
                          {formatFlics(user.flics)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' :
                          user.status === 'blocked' ? 'bg-red-100 text-red-800' :
                          user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">
                            Créé le: {creationDate}
                          </div>
                          <div className={`text-sm ${shouldHighlightExpired ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            {user.expires ? `Expire: ${user.expires}` : 'Pas d\'expiration'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">
                            {lastLoginDate.date}
                          </div>
                          <div className="text-sm text-gray-500">
                            {lastLoginDate.time}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleOpenEditDialog(user)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Modifier l'utilisateur"
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                          {user.status === 'active' ? (
                            <button
                              onClick={() => handleOpenStatusDialog(user, 'block')}
                              className="text-red-600 hover:text-red-900"
                              title="Désactiver l'utilisateur"
                            >
                              <BlockIcon className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleOpenStatusDialog(user, 'unblock')}
                              className="text-green-600 hover:text-green-900"
                              title="Réactiver l'utilisateur"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenDeleteDialog(user)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer définitivement l'utilisateur"
                          >
                            <DeleteIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <button
              onClick={() => handleChangePage(null, page + 1)}
              disabled={(page + 1) * rowsPerPage >= filteredUsers.length}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{page * rowsPerPage + 1}</span> à{' '}
                <span className="font-medium">
                  {Math.min((page + 1) * rowsPerPage, filteredUsers.length)}
                </span>{' '}
                sur <span className="font-medium">{filteredUsers.length}</span> résultats
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 0}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={(page + 1) * rowsPerPage >= filteredUsers.length}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </nav>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-sm text-gray-700">
            Total: {filteredUsers.length} utilisateur(s) trouvé(s)
          </p>
        </div>

        {/* Edit User Dialog */}
        {openEditDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Modifier l'utilisateur
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedUser?.email}
                </p>
              </div>

              <form onSubmit={handleEditSubmit}>
                <div className="px-6 py-4">
                  <div className="space-y-6">
                    {/* Group Selection */}
                    <div>
                      <label htmlFor="group" className="block text-sm font-medium text-gray-700 mb-1">
                        Groupe
                      </label>
                      <select
                        id="group"
                        value={editForm.group}
                        onChange={(e) => handleEditFormChange('group', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="">Sélectionner un groupe</option>
                        {[...new Set(roles.map(role => role.role))].map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>

                    {/* Subscription Selection */}
                    <div>
                      <label htmlFor="abo" className="block text-sm font-medium text-gray-700 mb-1">
                        Abonnement
                      </label>
                      <select
                        id="abo"
                        value={editForm.abo}
                        onChange={(e) => handleEditFormChange('abo', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="">Sélectionner un abonnement</option>
                        {subscriptions.map(subscription => (
                          <option key={subscription.id} value={subscription.id}>{subscription.id}</option>
                        ))}
                      </select>
                    </div>

                    {/* FLICs Management */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        FLICs
                      </label>
                      <div className="space-y-2">
                        {editForm.flics.map((flic, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={flic}
                              onChange={(e) => handleFlicChange(index, e.target.value)}
                              placeholder="ID FLIC"
                              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveFlic(index)}
                              className="text-red-600 hover:text-red-900 p-1"
                            >
                              <DeleteIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleAddFlic}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <AddIcon className="w-4 h-4 mr-1" />
                          Ajouter un FLIC
                        </button>
                      </div>
                    </div>

                    {/* Expiration Date */}
                    <div>
                      <label htmlFor="expires" className="block text-sm font-medium text-gray-700 mb-1">
                        Date d'expiration (dd.MM.yyyy)
                      </label>
                      <input
                        type="text"
                        id="expires"
                        value={editForm.expires}
                        onChange={(e) => handleEditFormChange('expires', e.target.value)}
                        placeholder="dd.MM.yyyy (laisser vide pour aucune expiration)"
                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                          editForm.expires && !validateExpirationDate(editForm.expires).isValid
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        }`}
                      />
                      {editForm.expires && !validateExpirationDate(editForm.expires).isValid ? (
                        <p className="mt-1 text-xs text-red-600">
                          {validateExpirationDate(editForm.expires).error}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-gray-500">
                          Format: dd.MM.yyyy (ex: 31.12.2024) ou laisser vide
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                  <button
                    type="button"
                    onClick={handleCloseEditDialog}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving || (editForm.expires && !validateExpirationDate(editForm.expires).isValid)}
                    className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                      ${!saving && (!editForm.expires || validateExpirationDate(editForm.expires).isValid) 
                        ? 'hover:bg-[rgb(121,192,189)]' 
                        : 'opacity-75 cursor-not-allowed'}
                      focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Dialog */}
        {openStatusDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {statusAction === 'block' ? 'Désactiver l\'utilisateur' : 'Réactiver l\'utilisateur'}
                </h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-500">
                  {statusAction === 'block' 
                    ? `Êtes-vous sûr de vouloir désactiver l'utilisateur ${selectedUser?.email} ?`
                    : `Êtes-vous sûr de vouloir réactiver l'utilisateur ${selectedUser?.email} ?`
                  }
                </p>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseStatusDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleStatusChange}
                  disabled={saving}
                  className={`inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    statusAction === 'block' 
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                      : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  } ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {saving 
                    ? 'Traitement...' 
                    : statusAction === 'block' 
                      ? 'Désactiver' 
                      : 'Réactiver'
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {formError && (
          <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
            {formError}
          </div>
        )}

        {formSuccess && (
          <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
            {statusAction === 'block' 
              ? 'Utilisateur désactivé avec succès' 
              : 'Utilisateur réactivé avec succès'
            }
          </div>
        )}

        {/* Delete User Confirmation Dialog */}
        {openDeleteDialog && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Supprimer l'utilisateur
                </h3>
              </div>

              <div className="px-6 py-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <DeleteIcon className="w-5 h-5 text-red-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      Attention : Cette action est irréversible
                    </h4>
                    <div className="mt-2 text-sm text-gray-500">
                      <p>
                        Êtes-vous sûr de vouloir supprimer définitivement l'utilisateur{' '}
                        <span className="font-medium text-gray-900">{selectedUser?.email}</span> ?
                      </p>
                      <p className="mt-2">
                        Cette action supprimera complètement l'utilisateur et toutes ses données associées.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseDeleteDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  disabled={saving}
                  className={`inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 bg-red-600 hover:bg-red-700 focus:ring-red-500 ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {saving ? 'Suppression...' : 'Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Create User Dialog */}
      {openCreateDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Créer un nouvel utilisateur
              </h3>
              
              <form onSubmit={handleCreateSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Email */}
                  <div>
                    <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="create-email"
                      value={createForm.email}
                      onChange={(e) => handleCreateFormChange('email', e.target.value)}
                      placeholder="utilisateur@exemple.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ Ce champ est définitif et ne pourra plus être changé
                    </p>
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="create-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Mot de passe *
                    </label>
                    <input
                      type="password"
                      id="create-password"
                      value={createForm.password}
                      onChange={(e) => handleCreateFormChange('password', e.target.value)}
                      placeholder="Minimum 8 caractères"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ Ce champ est définitif et ne pourra plus être changé
                    </p>
                  </div>

                  {/* Clinic */}
                  <div>
                    <label htmlFor="create-clinic" className="block text-sm font-medium text-gray-700 mb-1">
                      Clinique *
                    </label>
                    <input
                      type="text"
                      id="create-clinic"
                      value={createForm.clinic}
                      onChange={(e) => handleCreateFormChange('clinic', e.target.value)}
                      placeholder="Nom de la clinique"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ Ce champ est définitif et ne pourra plus être changé
                    </p>
                  </div>

                  {/* Group Selection */}
                  <div>
                    <label htmlFor="create-group" className="block text-sm font-medium text-gray-700 mb-1">
                      Groupe *
                    </label>
                    <select
                      id="create-group"
                      value={createForm.group}
                      onChange={(e) => handleCreateFormChange('group', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    >
                      <option value="">Sélectionner un groupe</option>
                      {[...new Set(roles.map(role => role.role))].map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>

                  {/* Subscription Selection */}
                  <div>
                    <label htmlFor="create-abo" className="block text-sm font-medium text-gray-700 mb-1">
                      Abonnement *
                    </label>
                    <select
                      id="create-abo"
                      value={createForm.abo}
                      onChange={(e) => handleCreateFormChange('abo', e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    >
                      <option value="">Sélectionner un abonnement</option>
                      {subscriptions.map(subscription => (
                        <option key={subscription.id} value={subscription.id}>{subscription.id}</option>
                      ))}
                    </select>
                  </div>

                  {/* Expiration Date */}
                  <div>
                    <label htmlFor="create-expires" className="block text-sm font-medium text-gray-700 mb-1">
                      Date d'expiration (dd.MM.yyyy)
                    </label>
                    <input
                      type="text"
                      id="create-expires"
                      value={createForm.expires}
                      onChange={(e) => handleCreateFormChange('expires', e.target.value)}
                      placeholder="dd.MM.yyyy (laisser vide pour aucune expiration)"
                      className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                        createForm.expires && !validateExpirationDate(createForm.expires).isValid
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                    />
                    {createForm.expires && !validateExpirationDate(createForm.expires).isValid ? (
                      <p className="mt-1 text-xs text-red-600">
                        {validateExpirationDate(createForm.expires).error}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        Format: dd.MM.yyyy (ex: 31.12.2024) ou laisser vide
                      </p>
                    )}
                  </div>
                </div>

                {/* FLICs Management */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    FLICs
                  </label>
                  <div className="space-y-2">
                    {createForm.flics.map((flic, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={flic}
                          onChange={(e) => handleCreateFlicChange(index, e.target.value)}
                          placeholder="ID FLIC"
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => handleCreateRemoveFlic(index)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <DeleteIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleCreateAddFlic}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <AddIcon className="w-4 h-4 mr-1" />
                      Ajouter un FLIC
                    </button>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseCreateDialog}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving || (createForm.expires && !validateExpirationDate(createForm.expires).isValid)}
                    className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                      ${!saving && (!createForm.expires || validateExpirationDate(createForm.expires).isValid) 
                        ? 'hover:bg-[rgb(121,192,189)]' 
                        : 'opacity-75 cursor-not-allowed'}
                      focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                  >
                    {saving ? 'Création...' : 'Créer l\'utilisateur'}
                  </button>
                </div>
              </form>

              {formError && (
                <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
                  {formError}
                </div>
              )}

              {formSuccess && (
                <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
                  Utilisateur créé avec succès
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


export default Users;
